import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import saveUndoRedoStateOperation from 'editor/src/store/editorModules/undoRedo/operation/saveUndoRedoStateOperation';
import type { Thunk, ThunkDispatch } from 'editor/src/store/hooks';
import isPageCountOptionsEnabled from 'editor/src/store/hostSettings/selector/isPageCountOptionsEnabled';
import getAllMatchingVariations from 'editor/src/store/variants/helpers/getAllMatchingVariations';
import changeEcommerceVariantPageCountOperation from 'editor/src/store/variants/operation/changeEcommerceVariantPageCountOperation';
import checkForMissingTemplatesOperation from 'editor/src/store/variants/operation/checkForMissingTemplatesOperation';
import getECommercePageCounts from 'editor/src/store/variants/selector/getECommercePageCounts';
import { setPageCountAction } from 'editor/src/store/variants/slice';

import { RootState } from 'editor/src/store';
import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import { removeSpreadAction, updateMediaElementAction } from '../../slice';
import getSpreadCountForIncrement from '../../util/getSpreadCountForIncrement';
import { legacyGetSpreadCountForIncrement } from '../../util/legacyPageCountRules';
import { canStepToPageCount, mergePageCountLimits } from '../../util/pageCountControls';
import planSpreadRemoval from '../../util/planSpreadRemoval';
import { getLoneSharedFieldMembers, getSharedTextFieldIdsWithPartners } from '../../util/sharedTextFields';

// Until the host sends `enablePageCountOptions`: removes the step spread by spread from the store,
// with no limit check of its own.
function removeLegacyGridStep(
  dispatch: ThunkDispatch,
  getState: () => RootState,
  spreadIndex: number,
  spreadsToRemove: number,
) {
  for (let i = 0; i < spreadsToRemove; i += 1) {
    const currentData = getState().design.designData;
    if (!currentData || currentData.spreads.length <= 1) {
      break;
    }
    // Removing a full grid step must not stop short — an off-grid count
    // would make the product revert to its minimum. Clamp to the shrinking
    // array (the clicked spread can sit within one increment of the end)
    // and walk back past non-removable spreads (the page math pins the
    // last inside spread of a spread book).
    let removeIndex = Math.min(spreadIndex, currentData.spreads.length - 1);
    while (removeIndex >= 0 && !currentData.spreads[removeIndex]?.canRemove) {
      removeIndex -= 1;
    }
    if (removeIndex < 0) {
      break;
    }
    dispatch(removeSpreadAction(removeIndex));
  }
}

const removeSpreadOperation =
  (spreadIndex: number): Thunk =>
  (dispatch, getState) => {
    batch(() => {
      const { designData } = getState().design;
      if (!designData) {
        return;
      }

      const pageCountOptionsEnabled = isPageCountOptionsEnabled(getState());
      // Mirror the add path: remove a full grid step worth of spreads so the
      // resulting page count stays on the valid grid. A non-removable target
      // yields an empty plan, so nothing below runs for it.
      const spreadsToRemove = pageCountOptionsEnabled
        ? getSpreadCountForIncrement(designData, spreadIndex, 'remove', getECommercePageCounts(getState()))
        : legacyGetSpreadCountForIncrement(designData, spreadIndex, getECommercePageCounts(getState()));
      const { removeIndexes, removedSpreads } = planSpreadRemoval(designData, spreadIndex, spreadsToRemove);
      if (!designData.spreads[spreadIndex]?.canRemove || (pageCountOptionsEnabled && !removeIndexes.length)) {
        return;
      }
      // The buttons gate on the same step; checked again so no caller can take the book below its
      // limits or off its grid.
      const limit = mergePageCountLimits(designData.page_count_limit, getECommercePageCounts(getState()));
      const removedPages = removedSpreads.reduce((pages, spread) => pages + spread.pages.length, 0);
      if (
        pageCountOptionsEnabled &&
        limit &&
        designData.page_count !== undefined &&
        !canStepToPageCount(designData, limit, designData.page_count - removedPages)
      ) {
        return;
      }

      // Which shared-field groups had a partner before any of this. Compared
      // against the leftovers afterwards so the sweep below clears only what
      // THIS removal broke: a design that arrived already carrying a stale
      // one-member id is not repaired here, because that write would be folded
      // into the "remove spread" undo entry and undo would put it back.
      const sharedBefore = new Set(getSharedTextFieldIdsWithPartners(getState().design.designData));

      dispatch(saveUndoRedoStateOperation('remove spread'));

      if (pageCountOptionsEnabled) {
        removeIndexes.forEach((removeIndex) => dispatch(removeSpreadAction(removeIndex)));
      } else {
        removeLegacyGridStep(dispatch, getState, spreadIndex, spreadsToRemove);
      }

      // A shared text field must never outlive its reason. Taking a spread away
      // takes its texts with it, and a group left holding one member is not
      // shared: the panel has no partner to name, so the id can be neither seen
      // nor cleared, and it would silently re-absorb the next layer that joined
      // it. Swept after the loop rather than per removal, because this removes
      // a whole grid step and can strand several groups at once.
      getLoneSharedFieldMembers(getState().design.designData)
        .filter(({ element }) => sharedBefore.has(element.extra.sharedFieldId as string))
        .forEach(({ element, address }) => {
          dispatch(
            updateMediaElementAction({
              elementAddress: address,
              elementUpdate: { extra: { ...element.extra, sharedFieldId: undefined } },
            }),
          );
        });

      let state = getState();
      if (state.design.designData?.page_count) {
        dispatch(setPageCountAction(state.design.designData.page_count));
        dispatch(changeEcommerceVariantPageCountOperation(state.design.designData.page_count));
        sendPostMessage('design.switchToProduct', {
          productUid: state.design.designData.product_uid,
          pageCount: state.design.designData.page_count,
        });

        state = getState();
        const { product, configuration, selectedMultiOptions, selectedSingleOptions, replaceControlKeys } =
          state.variants;
        if (!configuration.singleSelection) {
          const matchingVariations = getAllMatchingVariations(
            product.productVariations,
            selectedMultiOptions,
            selectedSingleOptions,
            false,
            replaceControlKeys,
          );
          dispatch(checkForMissingTemplatesOperation(matchingVariations));
        }
      }
    });
  };

export default removeSpreadOperation;
